import { Card, H6 } from '@blueprintjs/core';
import { showSaveSuccessToast } from 'Components/Generic/BOSToasterUtil';
import NoContentBox from 'Components/Generic/Misc/NoContentBox';
import { OBJECT_STATUS, user_org_roles } from 'Data/Enum';
import UserOrgRoleData from 'Data/UserOrgRoleData';
import DataManager from 'Lib/DataManager';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import styles from 'Styles/styles.module.css';
import { getNameWithInactiveTag, groupToMap } from 'Util';
import UserSelect from '../BOSFilterSelect/UserSelect';
import AddNewButton from '../Buttons/AddNewButton';
import DeleteButton from '../Buttons/DeleteButton';

let DM = new DataManager();

const OrgRoles = ({ orgId, editable = false }) => {
  const [refreshTrigger, setRefreshTrigger] = useState(false);
  const [rolesList, setRolesList] = useState([]);
  const [rolesMap, setRolesMap] = useState(new Map());
  const [selectedUserIdMap, setSelectedUserIdMap] = useState({});

  useEffect(() => {
    if (UserOrgRoleData) {
      DM.add(
        UserOrgRoleData.registerListener('LIST_LOADED', onRolesLoaded),
        UserOrgRoleData,
      );
    }
    if (UserOrgRoleData) {
      DM.add(
        UserOrgRoleData.registerListener('RECORD_CREATED', onRoleSaved),
        UserOrgRoleData,
      );
    }
    if (UserOrgRoleData) {
      DM.add(
        UserOrgRoleData.registerListener('RECORDS_DELETED', onRoleRemoved),
        UserOrgRoleData,
      );
    }
    return () => {
      DM.clear();
    };
  }, []);

  useEffect(() => {
    fetchRoleList();
  }, [refreshTrigger]);

  useEffect(() => {
    if (orgId) {
      fetchRoleList();
    }
  }, [orgId]);

  const fetchRoleList = () => {
    if (orgId) {
      UserOrgRoleData.getRecordList({
        where: { '*organization_id': orgId },
      });
    }
  };

  const onRolesLoaded = (payload) => {
    if (payload?.records?.length) {
      let rolesListSorted = [...payload.records]
        .filter((roleRecord) => !!roleRecord.user)
        .sort((a, b) => a.user.full_name.localeCompare(b.user.full_name));
      setRolesList(rolesListSorted);
    }
  };

  useEffect(() => {
    if (rolesList) {
      setRolesMap(groupToMap(rolesList, 'role'));
    }
  }, [rolesList]);

  const onRoleSaved = () => {
    showRolesSavedToast();
  };

  const onRoleRemoved = () => {
    showRolesSavedToast();
  };

  const showRolesSavedToast = () => {
    setRefreshTrigger((prevState) => !prevState);
    showSaveSuccessToast('Core Roles Updated');
  };

  const onRolesChanged = (userId, role, value) => {
    let newSelectedUserMap = { ...selectedUserIdMap };
    newSelectedUserMap[role] && delete newSelectedUserMap[role];
    setSelectedUserIdMap(newSelectedUserMap);
    let roleToSave = {
      role,
      user_id: userId,
      ...(orgId && { organization_id: orgId }),
    };
    if (value) {
      UserOrgRoleData.createRoleRecord(roleToSave);
    } else {
      UserOrgRoleData.deleteRoleRecord(roleToSave);
    }
  };

  let getUsersForRole = (role_key) => (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {!rolesMap.get(role_key)?.length && <NoContentBox label={'No Users'} />}
      {rolesMap.get(role_key)?.map((roleObject, index) => {
        return (
          <div
            key={index}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingTop: 6,
            }}>
            <span
              className={
                roleObject.user.status === OBJECT_STATUS.DEACTIVATED.key
                  ? styles.inactive
                  : undefined
              }>
              {getNameWithInactiveTag(roleObject.user, 'full_name')}
            </span>
            {editable && (
              <DeleteButton
                miniVersion
                useXIcon
                onDeleteItem={() =>
                  onRolesChanged(roleObject.user.id, role_key, false)
                }
                tooltipText={'Remove User from Role'}
              />
            )}
          </div>
        );
      })}
    </div>
  );

  const getNewRoleLine = (role_key) => (
    <div
      style={{
        paddingTop: 8,
        paddingBottom: 6,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 6,
      }}>
      <UserSelect
        excludedIds={rolesMap.get(role_key)?.map((role) => role.user.id) || []}
        onSelectUser={(user) => {
          setSelectedUserIdMap({ ...selectedUserIdMap, [role_key]: user.id });
        }}
        placeholder={'Select a user to add to role...'}
      />
      <AddNewButton
        miniVersion
        disabled={!selectedUserIdMap[role_key]}
        clickHandler={() =>
          selectedUserIdMap[role_key] &&
          onRolesChanged(selectedUserIdMap[role_key], role_key, true)
        }
        buttonText=''
      />
    </div>
  );

  let rolesTypeContent = Object.values(user_org_roles).map(
    (user_org_role, i) => {
      return (
        <div key={i}>
          <H6>{`${user_org_role.label}(s)`}</H6>
          <Card style={{ paddingTop: 6, paddingBottom: 6, minWidth: 300 }}>
            {getUsersForRole(user_org_role.key)}
            {editable && getNewRoleLine(user_org_role.key)}
          </Card>
        </div>
      );
    },
  );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 20,
      }}>
      {rolesTypeContent}
    </div>
  );
};

OrgRoles.propTypes = {
  orgId: PropTypes.number,
  editable: PropTypes.bool,
};

export default OrgRoles;
